/**
 * Fit-to-view für den Bild-Editor.
 *
 * Auf HiDPI / Windows-Skalierung (1.25 / 1.5, z. B. Lenovo X1 Carbon) liefert
 * ResizeObserver oft Bruchteil-Pixel. 1 px Unterschied am Viewport reicht, um
 * einen Roh-Fit von 70.45 % vs. 70.55 % zu erzeugen — die Anzeige springt dann
 * 70 ↔ 71, die Stage ändert sich um 1 px, der Viewport wieder, Feedbackloop.
 */

export const FIT_PAD_PX = 32;
/** 1-px- und Subpixel-Jitter (Scrollbar-Andeutung, fractional DPR) ignorieren. */
export const VIEWPORT_SLOP_PX = 2;

export type ViewportSize = { width: number; height: number };

export function quantizeFitScale(scale: number): number {
  if (!Number.isFinite(scale) || scale <= 0) return 1;
  return Math.max(0.05, Math.min(1, Math.round(scale * 100) / 100));
}

/** CSS-Pixel der Konva-Stage — dieselbe Rundung wie in AnnotationStage. */
export function stageCssSize(imagePx: number, scale: number): number {
  return Math.max(1, Math.round(imagePx * scale));
}

/**
 * Größter ganzer Prozent-Zoom, der in den Innenbereich passt.
 * 71 % wird verworfen, wenn die gerundete Stage 1 px zu groß wäre.
 */
export function computeFitScale(
  containerWidth: number,
  containerHeight: number,
  imageWidth: number,
  imageHeight: number,
  pad: number = FIT_PAD_PX,
): number {
  const innerW = containerWidth - pad;
  const innerH = containerHeight - pad;
  if (innerW <= 0 || innerH <= 0 || imageWidth <= 0 || imageHeight <= 0) return 1;

  const raw = Math.min(innerW / imageWidth, innerH / imageHeight, 1);
  let scale = quantizeFitScale(Math.max(0.05, raw));
  while (
    scale > 0.05
    && (stageCssSize(imageWidth, scale) > innerW || stageCssSize(imageHeight, scale) > innerH)
  ) {
    scale = quantizeFitScale(scale - 0.01);
  }
  return scale;
}

/** Gleiche Maße nicht neu setzen; 1–2 px / Bruchteil-Pixel nicht als Resize werten. */
export function stabilizeViewportSize(
  prev: ViewportSize,
  next: ViewportSize,
  slopPx: number = VIEWPORT_SLOP_PX,
): ViewportSize {
  const width = Math.round(next.width);
  const height = Math.round(next.height);
  if (prev.width <= 0 || prev.height <= 0) return { width, height };
  if (Math.abs(width - prev.width) <= slopPx && Math.abs(height - prev.height) <= slopPx) {
    return prev;
  }
  return { width, height };
}
